import { Controller } from '@hotwired/stimulus';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDebounce } from 'stimulus-use';

import { hide, show } from '../../lib/rs/utils';

export default class extends Controller {
  static values = {
    clientId: String,
    noResultsMessage: {
      type: String,
      default: 'No results found',
    },
    minQueryLength: {
      type: Number,
      default: 2,
    },
  };

  static targets = ['search', 'input', 'clear', 'dropdown'];

  static debounces = ['search'];

  connect() {
    useDebounce(this);

    if (this.searchTarget.value.length > 0 && !this.searchTarget.disabled) {
      this.search({ target: { value: this.searchTarget.value } });
    }
  }

  async search(event) {
    const query = event.target.value;

    if (query.length > this.minQueryLengthValue) {
      const brands = await this.#searchBrands(query);

      this.#renderBrands(brands);

      show(this.dropdownTarget, this.clearTarget);
    } else {
      hide(this.dropdownTarget, this.clearTarget);
    }
  }

  selectBrand(event) {
    this.#updateInputValue(event.params.id, event.params.name);
    this.#dispatchChangeEvent();

    hide(this.dropdownTarget);
  }

  reset() {
    this.#updateInputValue('', '');

    hide(this.dropdownTarget, this.clearTarget);
  }

  async #searchBrands(query) {
    const response = await fetch(
      `https://api.brandfetch.io/v2/search/${encodeURIComponent(query)}?c=${
        this.clientIdValue
      }`
    );

    return response.json();
  }

  #renderBrands(brands) {
    if (brands.length > 0) {
      const children = brands.map((brand) => this.#renderBrand(brand));

      this.dropdownTarget.replaceChildren(...children);
    } else {
      this.dropdownTarget.innerHTML = this.noResultsMessageValue;
      window.initFlowbite();
    }
  }

  #renderBrand(brand) {
    const container = document.createElement('template');

    container.innerHTML = `
      <div
        class="p-2 rounded-sm cursor-pointer hover:bg-neutral-50"
        data-action="click->brandfetch-autocomplete#selectBrand"
        data-brandfetch-autocomplete-id-param="${brand.brandId}"
        data-brandfetch-autocomplete-name-param="${brand.name}"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <img src="${brand.icon}" class="w-9 h-9 rounded-full shadow-xs" />
            <span>${brand.name}</span>
          </div>

          <span class="text-sm text-neutral-500">${brand.domain}</span>
        </div>
      </div>
    `;

    return container.content.firstElementChild;
  }

  #updateInputValue(id, name) {
    this.inputTarget.value = id;
    this.searchTarget.value = name;
  }

  // The underlying input element is a `hidden` input element, which does not emit any events.
  // As a result, we need to manually dispatch a `change` event to notify subscribers that the
  // value has changed.
  #dispatchChangeEvent() {
    this.inputTarget.dispatchEvent(
      new Event('change', { bubbles: true, cancelable: true })
    );
  }
}
